/** The operations rows: catalysts, data, journal, replay, registry, infra, memory, recovery, incidents. */
import {
  ContractViolation,
  bool,
  exact,
  field,
  instant,
  int,
  listOf,
  nat,
  nullable,
  oneOf,
  record,
  str,
  type Reader,
} from "./decode.ts";
import { LATCHES, SEVERITIES } from "./views.ts";

// --- the closed sets, mirroring `ssi.api.views_ops` (a Vitest test compares them) -----------

export const HEALTH_STATES = ["OK", "DEGRADED", "DOWN", "UNKNOWN"] as const;
export const LAG_FRESHNESS = ["FRESH", "STALE", "UNAVAILABLE"] as const;
export const CATALYST_CATEGORIES = [
  "RESULTS",
  "GUIDANCE",
  "CORPORATE_ACTION",
  "REGULATORY",
  "OTHER",
] as const;
export const NOVELTIES = ["NOVEL", "FOLLOW_UP", "DUPLICATE"] as const;
export const LEANINGS = ["POSITIVE", "NEGATIVE", "AMBIGUOUS", "NONE"] as const;
export const RELATIONS = ["ORIGINAL", "CORRECTION", "DUPLICATE", "NEW_GUIDANCE"] as const;
export const JOURNAL_KINDS = [
  "CANDIDATE",
  "REJECT",
  "ALLOCATION_MISS",
  "EXPIRY",
  "COMMAND",
  "ZERO_TRADE_DAY",
  "UNRESOLVED_EXPOSURE",
] as const;
export const RUN_STATUSES = [
  "REGISTERED",
  "QUEUED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
] as const;
export const REPRODUCIBILITY = ["REPRODUCED", "NOT_CHECKED", "MISMATCH"] as const;
export const CONFIG_STATUSES = ["IN_SYNC", "SAVED_NOT_ACTIVE", "MISSING_BINDING"] as const;
export const COMPONENTS = ["STRATEGY", "MODEL", "RULES"] as const;
export const PROBES = ["PASSING", "FAILING", "UNKNOWN"] as const;
export const RESOURCES = ["CPU", "DISK", "INODES", "IO", "NETWORK"] as const;
export const CHECK_RESULTS = ["PASSED", "FAILED", "NOT_RUN"] as const;
export const DEPLOY_PHASES = ["IDLE", "DEPLOYING", "VERIFYING", "ACCEPTED", "ROLLED_BACK"] as const;
export const PRESSURE_STATES = ["NORMAL", "ELEVATED", "HIGH", "CRITICAL"] as const;
export const CONTROLLER_RECOVERY = ["INTACT", "RECOVERING", "UNKNOWN"] as const;
export const CONTINUITY = ["CONTINUOUS", "GAP", "UNKNOWN"] as const;
export const DRILL_RESULTS = ["PASSED", "FAILED", "PARTIAL"] as const;
export const RECONCILIATIONS = ["NOT_STARTED", "IN_PROGRESS", "PASSED", "FAILED"] as const;
export const INCIDENT_STATUSES = ["OPEN", "MITIGATED", "CLOSED"] as const;
export const ROUTE_KINDS = ["APPLICATION", "EXTERNAL_HEARTBEAT", "EXTERNAL_ALERT"] as const;

/** The enumerations by the name of their Python `StrEnum`. */
export const OPS_ENUMS: Readonly<Record<string, readonly string[]>> = {
  HealthState: HEALTH_STATES,
  LagFreshness: LAG_FRESHNESS,
  CatalystCategory: CATALYST_CATEGORIES,
  Novelty: NOVELTIES,
  Leaning: LEANINGS,
  RelationKind: RELATIONS,
  JournalKind: JOURNAL_KINDS,
  RunStatus: RUN_STATUSES,
  Reproducibility: REPRODUCIBILITY,
  ConfigStatus: CONFIG_STATUSES,
  ComponentKind: COMPONENTS,
  Probe: PROBES,
  ResourceKind: RESOURCES,
  CheckResult: CHECK_RESULTS,
  DeployPhase: DEPLOY_PHASES,
  PressureState: PRESSURE_STATES,
  ControllerRecovery: CONTROLLER_RECOVERY,
  ContinuityState: CONTINUITY,
  DrillResult: DRILL_RESULTS,
  Reconciliation: RECONCILIATIONS,
  IncidentStatus: INCIDENT_STATUSES,
  RouteKind: ROUTE_KINDS,
};

export const BASIS_MAX = 10_000;
/** The operations projections `ssi.api.views_ops.VIEW_SCHEMAS_OPS` declares. */
export const OPS_VIEWS = [
  "catalysts",
  "data_health",
  "infra",
  "incidents",
  "journal",
  "memory",
  "recovery",
  "registry",
  "replay",
  "telemetry",
] as const;

// --- readers ---------------------------------------------------------------------------------

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const HEX64 = /^[0-9a-f]{64}$/;
const HEX40 = /^[0-9a-f]{40}$/;
const TEXT_MAX = 2000;

/** Read a calendar day (`YYYY-MM-DD`). */
export function day(value: unknown, path: string): string {
  const text = str(value, path);
  if (!DAY.test(text)) {
    throw new ContractViolation(path, "a calendar day");
  }
  return text;
}

function hexOf(pattern: RegExp, what: string): Reader<string> {
  return (value, path) => {
    const text = str(value, path);
    if (!pattern.test(text)) {
      throw new ContractViolation(path, what);
    }
    return text;
  };
}

/** Read non-empty text of at most 2,000 characters. */
export function text(value: unknown, path: string): string {
  const found = str(value, path);
  if (found === "" || found.length > TEXT_MAX) {
    throw new ContractViolation(path, "non-empty text of at most 2000 characters");
  }
  return found;
}

/** Read an `https:` URL (anything else would be shown as text, never as a link). */
export function https(value: unknown, path: string): string {
  const found = text(value, path);
  if (!found.startsWith("https://")) {
    throw new ContractViolation(path, "an https:// URL");
  }
  return found;
}

/** Read basis points, 0 to 10,000. */
export function basis(value: unknown, path: string): number {
  const found = nat(value, path);
  if (found > BASIS_MAX) {
    throw new ContractViolation(path, "basis points up to 10000");
  }
  return found;
}

/** Read a 0-100 score (a score, never a probability). */
export function score(value: unknown, path: string): number {
  const found = nat(value, path);
  if (found > 100) {
    throw new ContractViolation(path, "a score from 0 to 100");
  }
  return found;
}

/** Read a positive integer. */
export function pos(value: unknown, path: string): number {
  const found = nat(value, path);
  if (found < 1) {
    throw new ContractViolation(path, "a positive integer");
  }
  return found;
}

const hex64 = hexOf(HEX64, "a 64-digit lowercase hex digest");
const hex40 = hexOf(HEX40, "a 40-digit lowercase hex commit");

/** A reader whose path is optional, like the decoders of the other projections. */
type Row<T> = (value: unknown, path?: string) => T;

type Spec = Readonly<Record<string, readonly [string, Reader<unknown>]>>;
type Decoded<S extends Spec> = {
  readonly [K in keyof S]: S[K][1] extends Reader<infer T> ? T : never;
};

/** A reader for an object whose members are named by `spec` (camelCase name -> wire name, reader). */
function partOf<S extends Spec>(spec: S): Reader<Decoded<S>> {
  const keys = Object.values(spec).map(([wire]) => wire);
  return (value, path) => {
    const found = exact(value, path, keys);
    const out: Record<string, unknown> = {};
    for (const [name, [wire, read]] of Object.entries(spec)) {
      out[name] = field(found, path, wire, read);
    }
    return out as Decoded<S>;
  };
}

/** A reader for a row: `partOf(spec)` plus the `kind` discriminator. */
function rowOf<K extends string, S extends Spec>(
  kind: K,
  spec: S,
  check: (row: Decoded<S>, path: string) => void = () => undefined,
): Row<{ readonly kind: K } & Decoded<S>> {
  const read = partOf({ ...spec, kind: ["kind", oneOf([kind])] as const });
  return (value, path = "row") => {
    const row = read(value, path) as { readonly kind: K } & Decoded<S>;
    check(row, path);
    return row;
  };
}

function byKind<M extends Readonly<Record<string, Reader<{ readonly kind: string }>>>>(
  readers: M,
): Row<ReturnType<M[keyof M]>> {
  const kinds = Object.keys(readers);
  return (value, path = "row") => {
    const kind = field(record(value, path), path, "kind", oneOf(kinds));
    return (readers[kind] as Reader<ReturnType<M[keyof M]>>)(value, path);
  };
}

function contradiction(path: string, expected: string): never {
  throw new ContractViolation(path, expected);
}

// --- catalysts (U051-U053) -------------------------------------------------------------------

export const readRevision = partOf({
  revision: ["revision", pos],
  receivedAt: ["received_at", instant],
  classifiedAt: ["classified_at", instant],
  classificationVersion: ["classification_version", text],
  category: ["category", oneOf(CATALYST_CATEGORIES)],
  materialityScore: ["materiality_score", score],
  note: ["note", text],
} as const);
export type CatalystRevision = ReturnType<typeof readRevision>;

const catalystSpec = {
  itemId: ["item_id", text],
  listing: ["listing", text],
  headline: ["headline", text],
  sourceName: ["source_name", text],
  sourceUrl: ["source_url", https],
  publishedAt: ["published_at", instant],
  receivedAt: ["received_at", instant],
  processedAt: ["processed_at", instant],
  category: ["category", oneOf(CATALYST_CATEGORIES)],
  novelty: ["novelty", oneOf(NOVELTIES)],
  leaning: ["leaning", oneOf(LEANINGS)],
  materialityScore: ["materiality_score", score],
  materialityEvidence: ["materiality_evidence", text],
  classificationVersion: ["classification_version", text],
  relation: ["relation", oneOf(RELATIONS)],
  supersedes: ["supersedes", nullable(text)],
  duplicateGroup: ["duplicate_group", nullable(text)],
  revision: ["revision", pos],
  revisions: ["revisions", listOf(readRevision)],
} as const;

/** Decode a `catalysts` row; the revisions must end at the current one. */
export const decodeCatalystRow = rowOf("catalyst", catalystSpec, (row, path) => {
  const last = row.revisions.at(-1);
  if (last?.revision !== row.revision) {
    contradiction(`${path}.revisions`, "ending at the current revision");
  }
  if ((row.supersedes !== null) !== ["CORRECTION", "NEW_GUIDANCE"].includes(row.relation)) {
    contradiction(`${path}.supersedes`, "present exactly for a correction or new guidance");
  }
});
export type CatalystRow = ReturnType<typeof decodeCatalystRow>;

// --- data health (U056-U060, WC-02) ----------------------------------------------------------

const feedSpec = {
  feed: ["feed", text],
  state: ["state", oneOf(HEALTH_STATES)],
  lastEventAt: ["last_event_at", nullable(instant)],
  ageUs: ["age_us", nullable(nat)],
  sequenceGaps: ["sequence_gaps", nat],
  ingestLatencyUs: ["ingest_latency_us", nullable(nat)],
  classifyLatencyUs: ["classify_latency_us", nullable(nat)],
  clockOffsetUs: ["clock_offset_us", nullable(int)],
  affectedInstruments: ["affected_instruments", nat],
  evidenceRef: ["evidence_ref", text],
} as const;
const ruleSpec = {
  name: ["name", text],
  value: ["value", text],
  source: ["source", text],
  effectiveFrom: ["effective_from", day],
  effectiveTo: ["effective_to", nullable(day)],
  provenance: ["provenance", text],
} as const;
const coverageSpec = {
  dataset: ["dataset", text],
  observedPitBp: ["observed_pit_bp", basis],
  reconstructedBp: ["reconstructed_bp", basis],
  missingRequiredBp: ["missing_required_bp", basis],
  lineageRef: ["lineage_ref", text],
} as const;
const dependencySpec = {
  name: ["name", text],
  state: ["state", oneOf(HEALTH_STATES)],
  owner: ["owner", text],
  credentialExpiresOn: ["credential_expires_on", nullable(day)],
  quotaUsed: ["quota_used", nullable(nat)],
  quotaLimit: ["quota_limit", nullable(pos)],
  connections: ["connections", nat],
  licence: ["licence", text],
  retentionCovered: ["retention_covered", bool],
} as const;
const lagSpec = {
  projection: ["projection", text],
  epoch: ["epoch", nat],
  appliedLagUs: ["applied_lag_us", nullable(nat)],
  freshnessBoundUs: ["freshness_bound_us", pos],
  freshness: ["freshness", oneOf(LAG_FRESHNESS)],
} as const;
const queueSpec = {
  name: ["name", text],
  depth: ["depth", nat],
  oldestActionableAgeUs: ["oldest_actionable_age_us", nullable(nat)],
  failedMessages: ["failed_messages", nat],
  gaps: ["gaps", nat],
  recoveryRef: ["recovery_ref", text],
} as const;

/** Decode a `data_health` row; the `kind` field selects the shape. */
export const decodeDataHealthRow = byKind({
  feed: rowOf("feed", feedSpec, (row, path) => {
    if ((row.ageUs === null) !== (row.lastEventAt === null)) {
      contradiction(`${path}.age_us`, "an age exactly when there is a last event");
    }
    if (row.state === "OK" && (row.sequenceGaps > 0 || row.lastEventAt === null)) {
      contradiction(`${path}.state`, "OK only with no gaps and a last event");
    }
  }),
  rule: rowOf("rule", ruleSpec),
  coverage: rowOf("coverage", coverageSpec),
  dependency: rowOf("dependency", dependencySpec),
  projection_lag: rowOf("projection_lag", lagSpec, (row, path) => {
    if ((row.appliedLagUs === null) !== (row.freshness === "UNAVAILABLE")) {
      contradiction(`${path}.freshness`, "UNAVAILABLE exactly when the lag is absent");
    }
  }),
  queue: rowOf("queue", queueSpec),
});
export type DataHealthRow = ReturnType<typeof decodeDataHealthRow>;

/** Decode a `telemetry` row: the order-rate token bucket (WC-02). */
export const decodeOrderRateRow = rowOf("order_rate", {
  measuredAt: ["measured_at", instant],
  perSecondLimit: ["per_second_limit", pos],
  perMinuteLimit: ["per_minute_limit", pos],
  modificationCap: ["modification_cap", pos],
  exitReservePerSecond: ["exit_reserve_per_second", pos],
  peakPerSecond: ["peak_per_second", nat],
  sentLastMinute: ["sent_last_minute", nat],
  bucketTokens: ["bucket_tokens", nat],
  bucketCapacity: ["bucket_capacity", pos],
  saturationEvents: ["saturation_events", nat],
  entriesDenied: ["entries_denied", nat],
  exitsDenied: ["exits_denied", nat],
  exitsStarved: ["exits_starved", nat],
  lastSaturatedAt: ["last_saturated_at", nullable(instant)],
} as const);
export type OrderRateRow = ReturnType<typeof decodeOrderRateRow>;

// --- journal (U061, U062) --------------------------------------------------------------------

const journalSummarySpec = {
  exchangeDate: ["exchange_date", day],
  totalEntries: ["total_entries", nat],
  firstReceiptSeq: ["first_receipt_seq", nullable(pos)],
  lastReceiptSeq: ["last_receipt_seq", nullable(pos)],
  missingReceipts: ["missing_receipts", nat],
  complete: ["complete", bool],
  zeroTradeDay: ["zero_trade_day", bool],
  unresolvedExposure: ["unresolved_exposure", bool],
} as const;
const journalEntrySpec = {
  entryId: ["entry_id", text],
  exchangeDate: ["exchange_date", day],
  entryKind: ["entry_kind", oneOf(JOURNAL_KINDS)],
  listing: ["listing", nullable(text)],
  decisionId: ["decision_id", nullable(text)],
  orderId: ["order_id", nullable(text)],
  commandId: ["command_id", nullable(text)],
  eventId: ["event_id", nullable(text)],
  summary: ["summary", text],
  occurredAt: ["occurred_at", instant],
  receivedAt: ["received_at", instant],
  receiptSeq: ["receipt_seq", pos],
  revision: ["revision", pos],
} as const;

/** Decode a `journal` row (the day's completeness, or one entry). */
export const decodeJournalRow = byKind({
  journal_summary: rowOf("journal_summary", journalSummarySpec, (row, path) => {
    const empty = row.totalEntries === 0;
    if ((row.firstReceiptSeq === null) !== empty || (row.lastReceiptSeq === null) !== empty) {
      contradiction(path, "a receipt range exactly when there are entries");
    }
    const dense =
      (empty ||
        (row.firstReceiptSeq !== null &&
          row.lastReceiptSeq !== null &&
          row.lastReceiptSeq - row.firstReceiptSeq + 1 === row.totalEntries)) &&
      row.missingReceipts === 0;
    if (row.complete !== dense) {
      contradiction(`${path}.complete`, "complete exactly for a dense range with nothing missing");
    }
  }),
  journal_entry: rowOf("journal_entry", journalEntrySpec),
});
export type JournalRow = ReturnType<typeof decodeJournalRow>;
export type JournalEntry = Extract<JournalRow, { kind: "journal_entry" }>;
export type JournalSummary = Extract<JournalRow, { kind: "journal_summary" }>;

// --- replay (U066-U070): no result field exists ----------------------------------------------

/** Decode a `replay` row: a registered run's frozen inputs and state, never a result. */
export const decodeReplayRow = rowOf("replay_run", {
  runId: ["run_id", text],
  title: ["title", text],
  status: ["status", oneOf(RUN_STATUSES)],
  datasetRef: ["dataset_ref", hex64],
  universe: ["universe", text],
  strategyVersion: ["strategy_version", text],
  costVersion: ["cost_version", text],
  calendarVersion: ["calendar_version", text],
  fillVersion: ["fill_version", text],
  chronology: ["chronology", text],
  evaluationSplitUntouched: ["evaluation_split_untouched", bool],
  trialsAttempted: ["trials_attempted", nat],
  seeds: ["seeds", listOf(nat)],
  manifestHash: ["manifest_hash", hex64],
  reproducibility: ["reproducibility", oneOf(REPRODUCIBILITY)],
  unresolvedExits: ["unresolved_exits", nat],
  submittedAt: ["submitted_at", instant],
  startedAt: ["started_at", nullable(instant)],
  finishedAt: ["finished_at", nullable(instant)],
  failureReason: ["failure_reason", nullable(text)],
} as const);
export type ReplayRunRow = ReturnType<typeof decodeReplayRow>;

// --- registry (U071, U074) -------------------------------------------------------------------

const configSpec = {
  key: ["key", text],
  meaning: ["meaning", text],
  dependsOn: ["depends_on", listOf(text)],
  savedValue: ["saved_value", text],
  savedVersion: ["saved_version", pos],
  savedAt: ["saved_at", instant],
  activeValue: ["active_value", nullable(text)],
  activeVersion: ["active_version", nullable(pos)],
  activatedAt: ["activated_at", nullable(instant)],
  bindingRequired: ["binding_required", bool],
  status: ["status", oneOf(CONFIG_STATUSES)],
} as const;

/** The status a setting's values imply: a saved setting is active only if the active one equals it. */
export function derivedConfigStatus(row: {
  readonly savedValue: string;
  readonly savedVersion: number;
  readonly activeValue: string | null;
  readonly activeVersion: number | null;
  readonly bindingRequired: boolean;
}): (typeof CONFIG_STATUSES)[number] {
  if (row.activeValue === null) {
    return row.bindingRequired ? "MISSING_BINDING" : "SAVED_NOT_ACTIVE";
  }
  return row.activeValue === row.savedValue && row.activeVersion === row.savedVersion
    ? "IN_SYNC"
    : "SAVED_NOT_ACTIVE";
}

const activationSpec = {
  releaseId: ["release_id", text],
  component: ["component", oneOf(COMPONENTS)],
  activatedAt: ["activated_at", instant],
  previousRelease: ["previous_release", nullable(text)],
  active: ["active", bool],
  rollbackRef: ["rollback_ref", nullable(text)],
} as const;

/** Decode a `registry` row; a status that contradicts the values is refused (V08-A11). */
export const decodeRegistryRow = byKind({
  config: rowOf("config", configSpec, (row, path) => {
    if (derivedConfigStatus(row) !== row.status) {
      contradiction(`${path}.status`, `the status ${derivedConfigStatus(row)}`);
    }
  }),
  activation: rowOf("activation", activationSpec),
});
export type RegistryRow = ReturnType<typeof decodeRegistryRow>;
export type ConfigRow = Extract<RegistryRow, { kind: "config" }>;
export type ActivationRow = Extract<RegistryRow, { kind: "activation" }>;

// --- infrastructure (U076-U078) --------------------------------------------------------------

const serviceSpec = {
  service: ["service", text],
  node: ["node", text],
  intendedVersion: ["intended_version", text],
  runningVersion: ["running_version", text],
  replicaOwner: ["replica_owner", text],
  restartCount: ["restart_count", nat],
  liveness: ["liveness", oneOf(PROBES)],
  readiness: ["readiness", oneOf(PROBES)],
} as const;
const resourceSpec = {
  service: ["service", text],
  resource: ["resource", oneOf(RESOURCES)],
  usageBp: ["usage_bp", basis],
  trendBpPerHour: ["trend_bp_per_hour", int],
  measuredAt: ["measured_at", nullable(instant)],
  ageUs: ["age_us", nullable(nat)],
  freshness: ["freshness", oneOf(LAG_FRESHNESS)],
} as const;
const releaseSpec = {
  releaseId: ["release_id", text],
  commit: ["commit", hex40],
  githubRun: ["github_run", text],
  acceptedGeneration: ["accepted_generation", text],
  installedGeneration: ["installed_generation", text],
  hostCheck: ["host_check", oneOf(CHECK_RESULTS)],
  externalCheck: ["external_check", oneOf(CHECK_RESULTS)],
  schemaCompatible: ["schema_compatible", bool],
  phase: ["phase", oneOf(DEPLOY_PHASES)],
  maintenanceStart: ["maintenance_start", nullable(instant)],
  maintenanceEnd: ["maintenance_end", nullable(instant)],
  rollbackEligible: ["rollback_eligible", bool],
  previousRelease: ["previous_release", nullable(text)],
  tradingRearmed: ["trading_rearmed", bool],
} as const;
const freezeSpec = {
  engaged: ["engaged", bool],
  reason: ["reason", nullable(text)],
  since: ["since", nullable(instant)],
  readOnly: ["read_only", bool],
} as const;

/** Decode an `infra` row; a freeze switch that is not read-only is refused. */
export const decodeInfraRow = byKind({
  service: rowOf("service", serviceSpec, (row, path) => {
    if (row.readiness === "PASSING" && row.liveness !== "PASSING") {
      contradiction(`${path}.readiness`, "not ready without liveness");
    }
  }),
  resource: rowOf("resource", resourceSpec, (row, path) => {
    const unmeasured = row.measuredAt === null;
    if ((row.ageUs === null) !== unmeasured || (row.freshness === "UNAVAILABLE") !== unmeasured) {
      contradiction(path, "UNAVAILABLE exactly when there is no measurement");
    }
  }),
  release: rowOf("release", releaseSpec),
  freeze: rowOf("freeze", freezeSpec, (row, path) => {
    if (!row.readOnly) {
      contradiction(`${path}.read_only`, "the freeze switch read-only");
    }
  }),
});
export type InfraRow = ReturnType<typeof decodeInfraRow>;

// --- memory (U081-U083) ----------------------------------------------------------------------

const memorySpec = {
  scope: ["scope", text],
  availableBytes: ["available_bytes", nullable(nat)],
  usedBytes: ["used_bytes", nat],
  reservationBytes: ["reservation_bytes", nat],
  limitBytes: ["limit_bytes", pos],
  highWaterBytes: ["high_water_bytes", nat],
} as const;
const pressureSpec = {
  state: ["state", oneOf(PRESSURE_STATES)],
  ladderStep: ["ladder_step", nat],
  oomEvents: ["oom_events", nat],
  swapUsedBytes: ["swap_used_bytes", nat],
  stallBp: ["stall_bp", basis],
  lastRestartCause: ["last_restart_cause", nullable(text)],
  controllerRecovery: ["controller_recovery", oneOf(CONTROLLER_RECOVERY)],
  nextAction: ["next_action", text],
} as const;
const workloadSpec = {
  workerConcurrency: ["worker_concurrency", pos],
  concurrencyCap: ["concurrency_cap", pos],
  queuedBytes: ["queued_bytes", nat],
  inflightBytes: ["inflight_bytes", nat],
  oldestMessageAgeUs: ["oldest_message_age_us", nullable(nat)],
  cacheOccupancyBp: ["cache_occupancy_bp", basis],
  dbConnectionsUsed: ["db_connections_used", nat],
  dbConnectionsLimit: ["db_connections_limit", pos],
} as const;

/** Decode a `memory` row; the ladder step must be the state's position on the ladder. */
export const decodeMemoryRow = byKind({
  memory: rowOf("memory", memorySpec),
  pressure: rowOf("pressure", pressureSpec, (row, path) => {
    if (PRESSURE_STATES.indexOf(row.state) !== row.ladderStep) {
      contradiction(`${path}.ladder_step`, "the position of the state on the ladder");
    }
  }),
  workload: rowOf("workload", workloadSpec),
});
export type MemoryViewRow = ReturnType<typeof decodeMemoryRow>;

// --- recovery (U086-U090) --------------------------------------------------------------------

const backupSpec = {
  locationClass: ["location_class", text],
  uploadedThrough: ["uploaded_through", nullable(instant)],
  archiveContinuity: ["archive_continuity", oneOf(CONTINUITY)],
  archiveLagUs: ["archive_lag_us", nullable(nat)],
  verifiedThrough: ["verified_through", nullable(instant)],
  lastVerifiedDrill: ["last_verified_drill", nullable(text)],
  keyRecoveryReady: ["key_recovery_ready", bool],
  rpoTargetUs: ["rpo_target_us", pos],
  rpoMeasuredUs: ["rpo_measured_us", nullable(nat)],
  rtoTargetUs: ["rto_target_us", pos],
  rtoMeasuredUs: ["rto_measured_us", nullable(nat)],
} as const;
const drillSpec = {
  drillId: ["drill_id", text],
  at: ["at", instant],
  result: ["result", oneOf(DRILL_RESULTS)],
  missingSegments: ["missing_segments", nat],
  corruptionFindings: ["corruption_findings", nat],
  openFollowups: ["open_followups", nat],
  summary: ["summary", text],
} as const;
const restoreSpec = {
  epoch: ["epoch", nat],
  restoredAt: ["restored_at", nullable(instant)],
  reconciliation: ["reconciliation", oneOf(RECONCILIATIONS)],
  liveReady: ["live_ready", bool],
  runbookRef: ["runbook_ref", text],
  incidentOwner: ["incident_owner", nullable(text)],
} as const;

/** Decode a `recovery` row; verified beyond uploaded, or live-ready before reconciliation, is refused. */
export const decodeRecoveryRow = byKind({
  backup: rowOf("backup", backupSpec, (row, path) => {
    if (
      row.verifiedThrough !== null &&
      (row.uploadedThrough === null || row.verifiedThrough > row.uploadedThrough)
    ) {
      contradiction(`${path}.verified_through`, "no later than the upload");
    }
  }),
  drill: rowOf("drill", drillSpec, (row, path) => {
    if (row.result === "PASSED" && (row.missingSegments > 0 || row.corruptionFindings > 0)) {
      contradiction(`${path}.result`, "PASSED only with nothing missing or corrupt");
    }
  }),
  restore_epoch: rowOf("restore_epoch", restoreSpec, (row, path) => {
    if (row.liveReady && row.reconciliation !== "PASSED") {
      contradiction(`${path}.live_ready`, "false until the reconciliation passed");
    }
  }),
});
export type RecoveryRow = ReturnType<typeof decodeRecoveryRow>;

// --- incidents (U093, U094) ------------------------------------------------------------------

const readTimeline = partOf({ at: ["at", instant], text: ["text", text] } as const);
const readEscalation = partOf({
  level: ["level", pos],
  at: ["at", instant],
  to: ["to", text],
} as const);

const incidentSpec = {
  incidentId: ["incident_id", text],
  severity: ["severity", oneOf(SEVERITIES)],
  title: ["title", text],
  scope: ["scope", text],
  owner: ["owner", nullable(text)],
  onset: ["onset", instant],
  status: ["status", oneOf(INCIDENT_STATUSES)],
  acknowledged: ["acknowledged", bool],
  acknowledgedAt: ["acknowledged_at", nullable(instant)],
  snoozedUntil: ["snoozed_until", nullable(instant)],
  safetyLatch: ["safety_latch", oneOf(LATCHES)],
  timeline: ["timeline", listOf(readTimeline)],
  escalations: ["escalations", listOf(readEscalation)],
} as const;
const routeSpec = {
  routeId: ["route_id", text],
  name: ["name", text],
  routeKind: ["route_kind", oneOf(ROUTE_KINDS)],
  independent: ["independent", bool],
  lastHeartbeatAt: ["last_heartbeat_at", nullable(instant)],
  lastTestAt: ["last_test_at", nullable(instant)],
  lastTestResult: ["last_test_result", oneOf(CHECK_RESULTS)],
  state: ["state", oneOf(HEALTH_STATES)],
} as const;

/** Decode an `incidents` row; an external route must be independent, an application route not. */
export const decodeIncidentsRow = byKind({
  incident_detail: rowOf("incident_detail", incidentSpec, (row, path) => {
    if (row.acknowledged !== (row.acknowledgedAt !== null)) {
      contradiction(`${path}.acknowledged_at`, "present exactly when acknowledged");
    }
    if (
      row.snoozedUntil !== null &&
      (row.severity === "CRITICAL" || row.snoozedUntil <= row.onset)
    ) {
      contradiction(`${path}.snoozed_until`, "a snooze after onset, never on a CRITICAL incident");
    }
  }),
  route: rowOf("route", routeSpec, (row, path) => {
    if (row.independent !== (row.routeKind !== "APPLICATION")) {
      contradiction(`${path}.independent`, "independent exactly for an external route");
    }
    if ((row.lastTestAt === null) !== (row.lastTestResult === "NOT_RUN")) {
      contradiction(`${path}.last_test_at`, "present exactly when a test ran");
    }
  }),
});
export type IncidentsRow = ReturnType<typeof decodeIncidentsRow>;
export type IncidentDetail = Extract<IncidentsRow, { kind: "incident_detail" }>;
export type RouteStatus = Extract<IncidentsRow, { kind: "route" }>;

/** The row reader of each operations projection. */
export const OPS_ROWS: Readonly<Record<string, Reader<unknown>>> = {
  catalysts: decodeCatalystRow,
  data_health: decodeDataHealthRow,
  telemetry: decodeOrderRateRow,
  journal: decodeJournalRow,
  replay: decodeReplayRow,
  registry: decodeRegistryRow,
  infra: decodeInfraRow,
  memory: decodeMemoryRow,
  recovery: decodeRecoveryRow,
  incidents: decodeIncidentsRow,
};

/** The operations projection a fixture file is an envelope of (`envelope_<projection>[_<variant>]`). */
export function opsProjectionOf(name: string): string | undefined {
  const stem = name.startsWith("envelope_") ? name.slice("envelope_".length) : "";
  return OPS_VIEWS.find((view) => stem === view || stem.startsWith(`${view}_`));
}
